import { useEffect, useMemo } from 'react';
import { Router } from './Router';
import { useCookieScripts } from './hooks/useCookieScripts';
import { useCustomerChatScripts } from './hooks/useCustomerChatScripts';
import { useTrackingScripts } from './tracking/useTrackingScripts';
import { Helmet } from 'react-helmet';

import { ModalContextProvider } from './context/TodalContext';
import { GraphQLContextProvider } from './context/GraphQLContext';
import { DisplayContextProvider } from './context/DisplayContext';
import { EnergyOptionsContextProvider } from './context/EnergyOptionsContext';
import { EnvironmentContextProvider } from './context/EnvironmentContext';

import { useFinancingProvider } from './hooks/useFinancingProvider';
import { BrandingContextProvider } from './context/BrandingContext';
import { CustomerContextProvider } from './context/CustomerContext';
import { ErrorBoundary } from './pages/ErrorBoundary';

import { ToasterContextProvider } from './context/ToasterContext';
import { enableSentry } from './sentry';

import '~src/branding/base/base.css';
import '~src/branding/base/font.css';

enableSentry();

const App = () => {
  const trackingScripts = useTrackingScripts();
  const chatScripts = useCustomerChatScripts();
  const cookieScripts = useCookieScripts();

  const scripts = useMemo(
    () => [...trackingScripts, ...chatScripts, ...cookieScripts],
    [trackingScripts, chatScripts, cookieScripts]
  );

  useEffect(function updateViewPortMetaTag() {
    if (navigator.userAgent.includes('iPhone')) {
      document
        ?.querySelector('meta[name="viewport"]')
        ?.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1, shrink-to-fit=no');
    }
  }, []);

  useFinancingProvider();

  return (
    <ErrorBoundary>
      <ToasterContextProvider>
        <GraphQLContextProvider>
          <DisplayContextProvider>
            <EnvironmentContextProvider>
              <BrandingContextProvider>
                <CustomerContextProvider>
                  <ModalContextProvider>
                    <EnergyOptionsContextProvider>
                      <Helmet>
                        <title>Bodil Energiberegner</title>

                        <link rel='icon' type='image/png' href='~shared-assets/icons/favicon.ico' sizes='16x16' />

                        {scripts.map(({ async, defer, src, id }) => (
                          <script async={async} defer={defer} id={id} key={src} src={src} type='text/javascript' />
                        ))}
                      </Helmet>

                      <Router />
                    </EnergyOptionsContextProvider>
                  </ModalContextProvider>
                </CustomerContextProvider>
              </BrandingContextProvider>
            </EnvironmentContextProvider>
          </DisplayContextProvider>
        </GraphQLContextProvider>
      </ToasterContextProvider>
    </ErrorBoundary>
  );
};

export default App;
